import { useCallback, useEffect, useState } from 'react'

export type VatRateCategory = 'zero' | 'positive'

/** Copy from the SIB-1029 Figma state matrix. */
export const VAT_RATE_ALERT_COPY: Record<VatRateCategory, { title: string; description: string }> =
  {
    zero: {
      // Neutral until SIB-1147 separates Kleinunternehmer from reverse charge: 0 % is still
      // invoiced as reverse charge, so the copy must not name § 19 UStG.
      title: '0 % Umsatzsteuer',
      description:
        'Sie berechnen aktuell keine Umsatzsteuer. Bitte prüfen Sie, ob Ihre Rechnungstexte (Einstellungen > Rechnungen > Rechnungstexte) den entsprechenden gesetzlichen Hinweis enthalten.',
    },
    positive: {
      title: 'Umsatzsteuersatz über 0 %',
      description:
        'Sie weisen Umsatzsteuer auf Ihren Rechnungen aus. Bitte stellen Sie sicher, dass Ihre Rechnungstexte (Einstellungen > Rechnungen > Rechnungstexte) den aktuellen Steuersatz korrekt widerspiegeln und alle gesetzlichen Pflichtangaben enthalten.',
    },
  }

/** The two cases the Steuerdaten info alert distinguishes (SIB-1029). */
export function vatRateCategory(value: unknown): VatRateCategory | null {
  if (value === '' || value === null || value === undefined) return null
  const rate = Number(value)
  if (!Number.isFinite(rate) || rate < 0) return null
  return rate > 0 ? 'positive' : 'zero'
}

const storageKey = (userId: string) => `invoiceSettings.vatRateAlert.v1.${userId}`

function readDismissed(userId: string | undefined): VatRateCategory | null {
  if (!userId) return null
  try {
    const stored = localStorage.getItem(storageKey(userId))
    return stored === 'zero' || stored === 'positive' ? stored : null
  } catch {
    return null
  }
}

function writeDismissed(userId: string | undefined, category: VatRateCategory | null) {
  if (!userId) return
  try {
    if (category) localStorage.setItem(storageKey(userId), category)
    else localStorage.removeItem(storageKey(userId))
  } catch {
    // Storage unavailable: the dismissal then lasts for this visit only.
  }
}

/**
 * Which Steuerdaten info alert to show for the VAT rate currently in the field.
 *
 * A dismissal belongs to the category it was made in, not to a variant forever: "Verstanden"
 * at 5 % keeps the alert hidden at 8 %, but moving to 0 % and back shows each alert again. Only
 * a change of the field's category by the user lifts a dismissal; loading, re-rendering or
 * resetting to the same category does not. An empty or invalid entry while typing keeps the
 * last valid category instead of counting as 0 %.
 *
 * The dismissal is kept per user in this browser (no account-level preference exists), so it
 * does not follow the user to another device.
 */
export function useVatRateAlert(vatPercentage: unknown, userId: string | undefined) {
  const current = vatRateCategory(vatPercentage)
  const [lastValid, setLastValid] = useState<VatRateCategory | null>(current)
  const [dismissed, setDismissed] = useState<VatRateCategory | null>(() => readDismissed(userId))

  useEffect(() => {
    setDismissed(readDismissed(userId))
  }, [userId])

  useEffect(() => {
    if (current === null || current === lastValid) return
    // A first rate entered into empty legacy settings is not a change of category.
    if (lastValid !== null) {
      setDismissed(null)
      writeDismissed(userId, null)
    }
    setLastValid(current)
  }, [current, lastValid, userId])

  const category = current ?? lastValid

  const dismiss = useCallback(() => {
    if (!category) return
    setDismissed(category)
    writeDismissed(userId, category)
  }, [category, userId])

  return { visibleCategory: category && category !== dismissed ? category : null, dismiss }
}
